/**
 * Control Tower Override Log (P12)
 *
 * Append-only sink keyed by entityId. Records that an entity was touched by
 * a SuperAdmin override action so analysis selectors can flag/exclude it
 * from pilot signal.
 *
 * Hard rules:
 *   • Append-only. Past entries never mutated.
 *   • No PII, no contractValue, no pricing.
 *   • Never throws — telemetry must NOT block UI.
 *   • Separate from `contractorTelemetry` (contractor-side) and from
 *     `controlTowerAudit` (admin action ledger).
 */

export type ControlTowerOverrideEntityType =
  | "job"
  | "offer"
  | "contractor";

export type ControlTowerOverrideAction =
  | "assign_contractor"
  | "reassign_contractor"
  | "withdraw_offer"
  | "resend_offer"
  | "set_schedule_date"
  | "clear_schedule_date";

export interface ControlTowerOverrideRecord {
  entityType: ControlTowerOverrideEntityType;
  entityId: string;
  action: ControlTowerOverrideAction;
  adminId: string;
  timestamp: string;
}

const MAX_LOG = 1000;
const log: ControlTowerOverrideRecord[] = [];

export function recordControlTowerOverride(
  rec: Omit<ControlTowerOverrideRecord, "timestamp"> & { timestamp?: string },
): void {
  try {
    if (!rec.entityId || !rec.adminId) return;
    log.push({
      ...rec,
      timestamp: rec.timestamp ?? new Date().toISOString(),
    });
    if (log.length > MAX_LOG) log.shift();
  } catch {
    /* silent */
  }
}

export function getControlTowerOverrides(): ReadonlyArray<ControlTowerOverrideRecord> {
  return log;
}

export function wasOverridden(entityId: string): boolean {
  return log.some((r) => r.entityId === entityId);
}

export function getOverridesForEntity(
  entityId: string,
): ReadonlyArray<ControlTowerOverrideRecord> {
  return log.filter((r) => r.entityId === entityId);
}

/**
 * P12.1 — Override history for a single entity, sorted newest-first.
 * Read-only. Pure derivation from the append-only sink. Does not mutate
 * canonical Job / Offer / Contractor records.
 */
export function getOverrideHistory(
  entityId: string,
): ReadonlyArray<ControlTowerOverrideRecord> {
  return log
    .filter((r) => r.entityId === entityId)
    .slice()
    .sort((a, b) => (b.timestamp < a.timestamp ? -1 : b.timestamp > a.timestamp ? 1 : 0));
}

export function clearControlTowerOverrides(): void {
  log.length = 0;
}
